import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { API_URL } from '../config';
import { appInsights } from '../lib/appInsights';
import { marquerDeconnexionVolontaire } from '../utils/redirectionConnexion';


interface User {
  id: number;
  email: string;
  full_name: string;
  is_active: boolean;
  role?: {
    name: string;
    permissions: { key: string }[];
  };
  tenant_id?: number;
  transaction_fee_rate?: number;
  is_email_verified?: boolean;
  subscription_end_date?: string;
  preferred_theme?: 'light' | 'dark';
  tenant?: {
    name: string;
    plan?: string;
  };
  social_profile?: {
    pseudonym?: string;
    is_opt_in_leaderboard?: boolean;
    avatar_url?: string;
    display_real_name_on_forum?: boolean;
  };
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
  hasPermission: (permission: string) => boolean;
  sessionExpired: boolean;
  clearSessionExpired: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Vrai si le jeton est déjà expiré. Appeler /auth/logout avec un jeton mort renvoie 401 :
 * 284 échecs sur 320 appels en production (audit 2026-10-01, B04). La session serveur
 * n'existe plus, il n'y a rien à fermer.
 */
const jetonExpire = (jeton: string): boolean => {
  try {
    const charge = JSON.parse(atob(jeton.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof charge.exp === 'number' && charge.exp * 1000 <= Date.now();
  } catch {
    return false; // jeton illisible : on laisse le serveur trancher
  }
};

// 30 minutes in milliseconds
const INACTIVITY_TIMEOUT = 30 * 60 * 1000;

// Session Expired Modal Component
const SessionExpiredModal: React.FC<{ onClose: () => void; reason?: string }> = ({ onClose, reason }) => {
  const isNewDevice = reason?.includes('connexion plus récente');
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}>
      <div style={{
        backgroundColor: 'var(--bg-box)',
        borderRadius: '16px',
        padding: '32px',
        maxWidth: '420px',
        width: '90%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        border: '1px solid var(--border-color)',
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          margin: '0 auto 20px',
          borderRadius: '50%',
          background: isNewDevice
            ? 'linear-gradient(135deg, #ef4444, #dc2626)'
            : 'linear-gradient(135deg, #f59e0b, #d97706)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
        }}>
          {isNewDevice ? '📱' : '⏰'}
        </div>
        <h2 style={{
          margin: '0 0 12px',
          fontSize: '24px',
          fontWeight: 600,
          color: 'var(--text-heading)',
        }}>
          {isNewDevice ? 'Connexion depuis un autre appareil' : 'Session expirée'}
        </h2>
        <p style={{
          margin: '0 0 24px',
          color: 'var(--text-muted)',
          fontSize: '15px',
          lineHeight: 1.6,
        }}>
          {isNewDevice
            ? 'Votre compte a été connecté depuis un autre appareil ou navigateur. Pour votre sécurité, cette session a été terminée.'
            : "Votre session a expiré suite à une période d'inactivité. Veuillez vous reconnecter pour continuer."
          }
        </p>
        <button
          onClick={onClose}
          style={{
            width: '100%',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: 600,
            color: 'white',
            background: isNewDevice
              ? 'linear-gradient(135deg, #ef4444, #dc2626)'
              : 'linear-gradient(135deg, #4f46e5, #6366f1)',
            border: 'none',
            borderRadius: '10px',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = isNewDevice
              ? '0 8px 20px rgba(239, 68, 68, 0.4)'
              : '0 8px 20px rgba(79, 70, 229, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
        >
          Se reconnecter
        </button>
      </div>
    </div>
  );
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [sessionExpired, setSessionExpired] = useState<boolean>(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState<string>('');

  // Clear session expired state (called when user clicks reconnect)
  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false);
    window.location.href = '/login';
  }, []);

  // Logout function (internal, sets sessionExpired for modal)
  const handleSessionExpiry = useCallback(async (reason?: string) => {
    try {
      const currentToken = localStorage.getItem('access_token');
      if (currentToken && !jetonExpire(currentToken)) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Server-side logout failed:', error);
    } finally {
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      appInsights.clearAuthenticatedUserContext();
      setSessionExpiredReason(reason || '');
      setSessionExpired(true);
    }
  }, []);

  // Regular logout (manual, no modal)
  const logout = useCallback(async () => {
    marquerDeconnexionVolontaire();
    try {
      const currentToken = localStorage.getItem('access_token');
      if (currentToken && !jetonExpire(currentToken)) {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        });
      }
    } catch (error) {
      console.error('Server-side logout failed, proceeding with client-side logout:', error);
    } finally {
      localStorage.removeItem('access_token');
      setToken(null);
      setUser(null);
      appInsights.clearAuthenticatedUserContext();
      // No modal for manual logout
    }
  }, []);

  // Fetch user profile
  const fetchUserProfile = useCallback(
    async (currentToken: string, isSilent = false) => {
      if (!isSilent) setLoading(true);
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setToken(currentToken);
          appInsights.setAuthenticatedUserContext(userData.email);
        } else if (response.status === 401) {
          // Token expired ou invalidé (nouvelle connexion détectée) — lire le detail
          let reason = '';
          try {
            const errData = await response.json();
            reason = errData.detail || '';
          } catch { /* ignore */ }
          console.warn('[AUTH] 401 received:', reason);
          await handleSessionExpiry(reason);
        } else {
          // Erreur serveur transitoire (5xx, pool DB saturé, etc.) — PAS une preuve
          // de session invalide. Ne surtout pas déconnecter ici : seul un 401 explicite
          // (géré ci-dessus) signale un vrai problème de session/conflit de connexion.
          // On garde la session existante telle quelle ; un prochain appel réussira.
          console.warn(`[AUTH] /auth/me a échoué (HTTP ${response.status}), session conservée.`);
        }
      } catch (error) {
        // Erreur réseau/timeout — même principe : ne pas déconnecter sur un incident
        // de connectivité transitoire (ex. staging : pool DB à 3 connexions saturé par
        // les appels concurrents du dashboard).
        console.error('[AUTH] Erreur réseau lors de la vérification de session, session conservée:', error);
      } finally {
        setLoading(false);
      }
    },
    [handleSessionExpiry]
  );

  // Login function
  const login = (newToken: string, newRefreshToken?: string) => {
    localStorage.setItem('access_token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }
    setSessionExpired(false);
    fetchUserProfile(newToken);
  };

  // Verify token on initial component mount
  useEffect(() => {
    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      fetchUserProfile(storedToken);
    } else {
      setLoading(false);
    }
  }, [fetchUserProfile]);

  // Inactivity Timer
  useEffect(() => {
    if (!token) return;

    let timeoutId: NodeJS.Timeout;

    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('User inactive for 30 minutes. Showing expiration modal.');
        handleSessionExpiry();
      }, INACTIVITY_TIMEOUT);
    };

    const events: (keyof WindowEventMap)[] = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer(); // Initialize timer

    return () => {
      clearTimeout(timeoutId);
      events.forEach((event) => window.removeEventListener(event, resetTimer));
    };
  }, [token, handleSessionExpiry]);

  // Silent Token Renewal (every 25 min to stay ahead of 30 min JWT expiry)
  useEffect(() => {
    if (!token) return;

    const RENEW_INTERVAL = 25 * 60 * 1000; // 25 minutes

    const renewToken = async () => {
      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) return;

        const response = await fetch(`${API_URL}/auth/refresh?token=${refreshToken}`, {
          method: 'POST'
        });

        if (response.ok) {
          const data = await response.json();
          localStorage.setItem('access_token', data.access_token);
          setToken(data.access_token);
          console.log('[AUTH] Token refreshed silently.');
        } else if (response.status === 401) {
          // Refresh token expired or invalid -> force logout
          console.warn('[AUTH] Refresh token expired. Logging out.');
          handleSessionExpiry();
        }
      } catch (error) {
        console.warn('[AUTH] Silent token refresh failed:', error);
      }
    };

    const intervalId = setInterval(renewToken, RENEW_INTERVAL);

    return () => clearInterval(intervalId);
  }, [token]);

  const hasPermission = (permission: string): boolean => {
    if (!user || !user.role) return false;
    // global_admin has all permissions implicitly
    if (user.role.name === 'global_admin') return true;
    return user.role.permissions.some((p) => p.key === permission);
  };

  // Refresh user function to be exposed
  const refreshUser = useCallback(async () => {
    const currentToken = localStorage.getItem('access_token');
    if (currentToken) {
      await fetchUserProfile(currentToken, true); // Silent refresh
    }
  }, [fetchUserProfile]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        login,
        logout,
        isAuthenticated: !!token,
        loading,
        hasPermission,
        sessionExpired,
        clearSessionExpired,
        refreshUser
      }}
    >
      {children}
      {sessionExpired && <SessionExpiredModal onClose={clearSessionExpired} reason={sessionExpiredReason} />}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
